import React, { useState, useEffect, useRef, useCallback, ChangeEvent, KeyboardEvent } from 'react';
import './Dropdown.scss';
import { Keyboard } from '@/types';

interface Option {
  label: any;
  value: string;
  searchKey?: string;
  showCondition?: boolean;
  isTitle?: boolean;
}

interface DropdownProps {
  value?: string;
  options: Option[];
  onChange?: Function;
  placeholder: any;
  placeholderInOption?: boolean;
  className?: string;
  disabled?: boolean;
  onToggleOptions?: Function;
  autoComplete?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  value,
  options,
  onChange,
  onToggleOptions,
  placeholder,
  placeholderInOption,
  className,
  disabled,
  autoComplete,
}) => {
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([]);
  const [openOptions, setOpenOptions] = useState<boolean>(false);
  const [targetOptionIndex, setTargetOptionIndex] = useState<number>(-1);
  const [inputValue, setInputValue] = useState<string>('');
  const [typeMode, setTypeMode] = useState<boolean>(false);
  const dropdown = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const handleClickLabel = (e: Event | React.BaseSyntheticEvent) => {
    e.stopPropagation();
    toggleOptions();
    autoComplete && setTypeMode(true);
    inputRef && inputRef.current && inputRef.current.focus();
  };

  const toggleOptions = useCallback(() => {
    setOpenOptions(!openOptions);
    typeMode && setTypeMode(false);
  }, [openOptions, typeMode]);

  const handleChangeOption = (option: Option) => {
    onChange && onChange(option);
    toggleOptions();
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (typeMode) {
      const { value } = e.target;
      setInputValue(value);
      const optionsMatch = options.filter((option) => option.searchKey?.toLowerCase().includes(value.toLowerCase()));
      setTargetOptionIndex(-1);
      setFilteredOptions(optionsMatch);
    }
  };

  const handleBlurInput = () => {
    setTypeMode(false);
  };

  const handleFocusInput = () => {
    setOpenOptions(true);
    autoComplete && !inputValue && setTargetOptionIndex(-1);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    let newTargetOptionIndex: number = targetOptionIndex;
    const pressArrowDownBtn = e.key === Keyboard.arrowDown;
    const pressArrowUpBtn = e.key === Keyboard.arrowUp;
    const pressEnterBtn = e.key === Keyboard.enter;
    const lastItem = targetOptionIndex >= filteredOptions.length - 1;
    const firstItem = targetOptionIndex < 0;
    pressArrowDownBtn && !lastItem && (newTargetOptionIndex = targetOptionIndex + 1);
    pressArrowUpBtn && !firstItem && (newTargetOptionIndex = targetOptionIndex - 1);
    setTargetOptionIndex(newTargetOptionIndex);
    const optionMatch = filteredOptions.find((option, index) => index === newTargetOptionIndex);
    if (pressEnterBtn) {
      handleChangeOption(optionMatch || { label: placeholder, value: '' });
      setTypeMode(false);
    }
  };

  const selectedValue = () => {
    const selectedOption = options.find((option) => option.value === value);
    return selectedOption ? selectedOption.label : placeholder;
  };

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      const outside = dropdown.current && !dropdown?.current.contains(e.target as HTMLInputElement);
      outside && openOptions && toggleOptions();
    },
    [openOptions, toggleOptions]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    onToggleOptions && onToggleOptions(openOptions);
  }, [onToggleOptions, openOptions]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  return (
    <div className={`Dropdown ${className} ${disabled && 'disabled'}`} ref={dropdown}>
      <div className={`Dropdown-input ${!typeMode && 'hidden'}`}>
        <input
          onChange={handleInput}
          onBlur={handleBlurInput}
          onFocus={handleFocusInput}
          value={inputValue}
          ref={inputRef}
          onKeyDownCapture={handleInputKeyDown}
          autoComplete="off"
        />
      </div>
      <div className={`Dropdown-label ${typeMode && 'hidden'}`} onClick={handleClickLabel}>
        {selectedValue()}
      </div>
      <div className={`Dropdown-options ${openOptions && 'open'}`} ref={optionsRef}>
        {placeholder && placeholderInOption && (
          <div
            className={`Dropdown-options-item ${targetOptionIndex < 0 && 'hover'}`}
            onClick={() => handleChangeOption({ label: placeholder, value: '' })}
            onMouseEnter={() => setTargetOptionIndex(-1)}
          >
            {placeholder}
          </div>
        )}
        {filteredOptions.map(
          (option, index) =>
            (option.showCondition === undefined || option.showCondition) && (
              <div
                key={option.value}
                className={`
                  Dropdown-options-item
                  ${index === targetOptionIndex && 'hover'}
                  ${option.isTitle && 'isTitle'}
                `}
                onClick={() => handleChangeOption(option)}
                onMouseEnter={() => setTargetOptionIndex(index)}
              >
                {option.label}
              </div>
            )
        )}
      </div>
    </div>
  );
};

export default Dropdown;
